<template>
	<section class="page-play">
		<!-- Loading state while trainer initializes -->
		<section v-if="isLoading" class="trainer-loading">
			<img src="~@chalkysticks/sass/build/asset/image/loader/triangle-white.svg" />
			<p>Loading...</p>
		</section>

		<!-- Error state if trainer fails to load -->
		<section v-else-if="loadError" class="trainer-error">
			<h2>Unable to load trainer</h2>
			<p>{{ loadError }}</p>
			<button v-on:click="retryLoad" class="retry-button">Try Again</button>
		</section>

		<!-- Trainer content once loaded -->
		<section v-else class="trainer-container">
			<ViewPlayTrainer v-bind:diagramId="diagramId" />
		</section>
	</section>
</template>

<script lang="ts">
	import * as Event from '@chalkysticks/vue2-trainer/src/App/Event';
	import * as Service from '../Service';
	import ChalkySticks, { type IDispatcherEvent } from '@chalkysticks/sdk';
	import PageBase from '@/Core/Page.vue';
	import { Component } from 'vue-property-decorator';
	import { beforeDestroy, bind, created, mounted } from '@/Utility/Decorators';

	/**
	 * Page controller for the 3D pool trainer. Manages loading state,
	 * keyboard/visibility events, and diagram deep-linking.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Page
	 * @project ChalkySticks App
	 */
	@Component
	export default class PagePlay extends PageBase {
		/**
		 * Diagram ID extracted from route params for deep-linking
		 *
		 * @return string | null
		 */
		protected get diagramId(): string | null {
			return this.$route.params.id ? (this.$route.params.id as string) : null;
		}

		/**
		 * Whether the trainer 3D world is still loading
		 *
		 * @type boolean
		 */
		protected isLoading: boolean = true;

		/**
		 * Error message if trainer fails to load
		 *
		 * @type string | null
		 */
		protected loadError: string | null = null;

		/**
		 * Initialize trainer on component creation. Dynamically imports
		 * the trainer chunk and calls Vue.use() if not already installed.
		 *
		 * @return Promise<void>
		 */
		@created
		protected async initializeTrainer(): Promise<void> {
			this.isLoading = true;
			this.loadError = null;

			try {
				await Service.TrainerLoader.initialize();
				this.isLoading = false;
			} catch (error) {
				console.error('[PageTrainer] Failed to initialize trainer:', error);
				this.loadError = 'Failed to load trainer. Please try again.';
				this.isLoading = false;
			}
		}

		/**
		 * Attach keyboard and visibility event listeners on mount.
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			document.addEventListener('visibilitychange', this.Handle_OnVisibilityChange);

			// Suspend geolocation to free resources for the 3D trainer
			Service.Location.suspend();
		}

		/**
		 * Detach keyboard and visibility event listeners before destroy.
		 *
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			document.removeEventListener('visibilitychange', this.Handle_OnVisibilityChange);

			// Resume geolocation if it was suspended
			Service.Location.resume();
		}

		/**
		 * Retry loading the trainer after an error
		 *
		 * @return Promise<void>
		 */
		protected async retryLoad(): Promise<void> {
			Service.TrainerLoader.reset();

			await this.initializeTrainer();
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Hide loading state when the trainer 3D world reports ready.
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnGameReady(): Promise<void> {
			this.isLoading = false;
		}

		/**
		 * Mute/unmute trainer audio when the browser tab visibility changes.
		 *
		 * @param Event e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnVisibilityChange(e: Event): Promise<void> {
			const isHidden = document.hidden;

			ChalkySticks.Core.Event.Bus.dispatch(Event.Audio.SetEnabled, !isHidden);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.page-play {
		background-color: #000000;
		height: 100%;
		overflow: hidden;
		position: fixed;
		width: 100%;

		.trainer-loading {
			align-items: center;
			display: flex;
			flex-direction: column;
			height: 100%;
			justify-content: center;
			text-align: center;

			img {
				height: 48px;
				width: 48px;
			}

			p {
				color: #666;
				font-size: 1.2rem;
				margin-top: 1rem;
			}
		}

		.trainer-container {
			background-size: 50px 50px;
			background: url('~@chalkysticks/sass/build/asset/image/loader/triangle-white.svg') center center no-repeat;
			height: 100%;
			width: 100%;
		}

		.chalky.utility-settings-panel {
			padding-top: var(--global-header-size);
		}
	}

	// State
	// ---------------------------------------------------------------------------

	#app[page='PagePlay'] {
		--global-tab-height: 0;

		#GlobalNavigationTabs {
			display: none;
		}
	}

	// Specifically targets devices where the primary input is touch and cannot hover
	@media (hover: none) and (pointer: coarse) {
		#app[page='PagePlay'] .ui-tooltip {
			display: none !important;
		}
	}
</style>
